/**
 * UndoRedoService - Servicio de deshacer/rehacer para operaciones de mapa
 *
 * @version 1.0
 * @updated 2026-02-19
 * @module core/map-tools
 *
 * @description
 * Implementa un sistema de undo/redo con historial de operaciones.
 * Soporta comandos reversibles para crear, editar y eliminar elementos.
 *
 * @remarks
 * **Características**:
 * - Stack de historial con límite configurable (default: 50 operaciones)
 * - Comandos con patrón Command (execute, undo, redo)
 * - Shortcuts de teclado: Ctrl+Z (undo), Ctrl+Y o Ctrl+Shift+Z (redo)
 * - Estado observable (para actualizar UI)
 *
 * **Tipos de operaciones soportadas**:
 * - CreateElement: Crear árbol, parque, etc.
 * - DeleteElement: Eliminar elemento
 * - UpdateElement: Modificar propiedades
 * - MoveElement: Cambiar posición (futuro)
 *
 * @example
 * ```typescript
 * import { undoRedoService } from '@/core/map-tools';
 *
 * // Registrar una operación de creación
 * const command: UndoCommand = {
 *   type: 'create',
 *   elementType: 'tree',
 *   data: { id: 'tree-123', ...treeData },
 *   execute: () => { addTree(treeData) },
 *   undo: () => { removeTree('tree-123') },
 * };
 * undoRedoService.execute(command);
 *
 * // Deshacer última operación
 * undoRedoService.undo();
 *
 * // Rehacer operación deshecha
 * undoRedoService.redo();
 * ```
 */

/**
 * Tipo de operación
 */
export type OperationType = 'create' | 'delete' | 'update' | 'move';

/**
 * Tipo de elemento
 */
export type ElementType = 'tree' | 'park' | 'fountain' | 'sunshade' | 'water-course' | 'water-mirror' | 'grid' | 'container';

/**
 * Comando reversible
 */
export interface UndoCommand {
  /**
   * Tipo de operación
   */
  type: OperationType;

  /**
   * Tipo de elemento afectado
   */
  elementType: ElementType;

  /**
   * Datos del elemento (para logging/debugging)
   */
  data: any;

  /**
   * Descripción de la operación (para UI)
   */
  description?: string;

  /**
   * Ejecuta la operación
   */
  execute: () => void;

  /**
   * Deshace la operación
   */
  undo: () => void;

  /**
   * Re-ejecuta la operación (opcional, por defecto usa execute)
   */
  redo?: () => void;
}

/**
 * Callback para cambios en el historial
 */
export type HistoryChangeCallback = (state: { canUndo: boolean; canRedo: boolean }) => void;

/**
 * Servicio de Undo/Redo
 */
class UndoRedoService {
  /**
   * Stack de comandos ejecutados (historial de undo)
   */
  private undoStack: UndoCommand[] = [];

  /**
   * Stack de comandos deshechos (historial de redo)
   */
  private redoStack: UndoCommand[] = [];

  /**
   * Límite máximo de operaciones en el historial
   */
  private readonly MAX_HISTORY = 50;

  /**
   * Listeners para cambios en el historial
   */
  private listeners: Set<HistoryChangeCallback> = new Set();

  /**
   * Flag para indicar si se está ejecutando un undo/redo
   * (para evitar loops infinitos)
   */
  private isExecuting = false;

  /**
   * Ejecuta un comando y lo agrega al historial
   */
  execute(command: UndoCommand): void {
    if (this.isExecuting) {
      console.warn('[UndoRedoService] Cannot execute command during undo/redo');
      return;
    }

    try {
      this.isExecuting = true;

      // Ejecutar el comando
      command.execute();

      // Agregar al historial de undo
      this.undoStack.push(command);

      // Limpiar historial de redo (nueva acción invalida el redo)
      this.redoStack = [];

      // Limitar tamaño del historial
      if (this.undoStack.length > this.MAX_HISTORY) {
        this.undoStack.shift(); // Eliminar el comando más antiguo
      }

      console.log(
        `[UndoRedoService] Executed: ${command.type} ${command.elementType}`,
        { canUndo: this.canUndo(), canRedo: this.canRedo() }
      );

      this.notifyListeners();
    } finally {
      this.isExecuting = false;
    }
  }

  /**
   * Deshace la última operación
   */
  undo(): void {
    if (!this.canUndo() || this.isExecuting) {
      console.warn('[UndoRedoService] Cannot undo');
      return;
    }

    try {
      this.isExecuting = true;

      // Obtener último comando
      const command = this.undoStack.pop()!;

      // Deshacer
      command.undo();

      // Mover al stack de redo
      this.redoStack.push(command);

      console.log(
        `[UndoRedoService] Undone: ${command.type} ${command.elementType}`,
        { canUndo: this.canUndo(), canRedo: this.canRedo() }
      );

      this.notifyListeners();
    } finally {
      this.isExecuting = false;
    }
  }

  /**
   * Rehace la última operación deshecha
   */
  redo(): void {
    if (!this.canRedo() || this.isExecuting) {
      console.warn('[UndoRedoService] Cannot redo');
      return;
    }

    try {
      this.isExecuting = true;

      // Obtener último comando deshecho
      const command = this.redoStack.pop()!;

      // Re-ejecutar (usar redo si existe, sino execute)
      if (command.redo) {
        command.redo();
      } else {
        command.execute();
      }

      // Mover de vuelta al stack de undo
      this.undoStack.push(command);

      console.log(
        `[UndoRedoService] Redone: ${command.type} ${command.elementType}`,
        { canUndo: this.canUndo(), canRedo: this.canRedo() }
      );

      this.notifyListeners();
    } finally {
      this.isExecuting = false;
    }
  }

  /**
   * Verifica si hay operaciones para deshacer
   */
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Verifica si hay operaciones para rehacer
   */
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Limpia todo el historial
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    console.log('[UndoRedoService] History cleared');
    this.notifyListeners();
  }

  /**
   * Obtiene el estado actual del historial
   */
  getState(): { canUndo: boolean; canRedo: boolean; undoCount: number; redoCount: number } {
    return {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      undoCount: this.undoStack.length,
      redoCount: this.redoStack.length,
    };
  }

  /**
   * Obtiene descripción de la próxima operación a deshacer
   */
  getUndoDescription(): string | null {
    if (!this.canUndo()) return null;
    const command = this.undoStack[this.undoStack.length - 1];
    return command.description || `${command.type} ${command.elementType}`;
  }

  /**
   * Obtiene descripción de la próxima operación a rehacer
   */
  getRedoDescription(): string | null {
    if (!this.canRedo()) return null;
    const command = this.redoStack[this.redoStack.length - 1];
    return command.description || `${command.type} ${command.elementType}`;
  }

  /**
   * Registra un listener para cambios en el historial
   */
  onChange(callback: HistoryChangeCallback): () => void {
    this.listeners.add(callback);

    // Retornar función de cleanup
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notifica a todos los listeners sobre cambios en el historial
   */
  private notifyListeners(): void {
    const state = {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
    };

    this.listeners.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('[UndoRedoService] Error in listener:', error);
      }
    });
  }
}

/**
 * Instancia singleton del servicio
 */
export const undoRedoService = new UndoRedoService();
